import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import AdminLayout from "./AdminLayout";
import { Card } from "./_shared";
import { MODE_META, COVER_FIELDS } from "./cover/constants";
import CoverToggleCard from "./cover/CoverToggleCard";
import CoverModeTabs from "./cover/CoverModeTabs";
import CoverEditor from "./cover/CoverEditor";
import CoverPreview from "./cover/CoverPreview";

export default function AdminCoverPages() {
  const [loading, setLoading] = useState(true);
  const [modes, setModes] = useState([]);
  const [validTones, setValidTones] = useState([]);
  const [activeMode, setActiveMode] = useState("coming_soon");
  // Currently-live site mode (mirrors backend SITE_MODE; 'live' = cover off).
  const [siteMode, setSiteMode] = useState("live");
  const [togglingSite, setTogglingSite] = useState(false);

  // Drafts keyed by mode so users can flip tabs without losing edits.
  const [drafts, setDrafts] = useState({});
  const [savingMode, setSavingMode] = useState(null);
  const [resettingMode, setResettingMode] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [coversRes, modeRes] = await Promise.all([
        api.get("/admin/cover-pages"),
        api.get("/site/mode"),
      ]);
      const data = coversRes.data;
      setModes(data.modes || []);
      setValidTones(data.valid_tones || []);
      const next = {};
      (data.modes || []).forEach((m) => {
        next[m.mode] = { ...m };
      });
      setDrafts(next);
      const currentMode = (modeRes.data?.mode || "live").toLowerCase();
      setSiteMode(currentMode);
      if (["coming_soon", "maintenance", "custom"].includes(currentMode)) {
        setActiveMode(currentMode);
      }
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Failed to load cover pages.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const draft = useMemo(() => drafts[activeMode] || {}, [drafts, activeMode]);
  const setField = (field, value) =>
    setDrafts((d) => ({
      ...d,
      [activeMode]: { ...(d[activeMode] || {}), [field]: value },
    }));

  const saved = useMemo(
    () => modes.find((m) => m.mode === activeMode) || {},
    [modes, activeMode],
  );

  const isDirty = useMemo(
    () => COVER_FIELDS.some((k) => (draft[k] || "") !== (saved[k] || "")),
    [draft, saved],
  );

  const onSave = async () => {
    setSavingMode(activeMode);
    try {
      const payload = Object.fromEntries(
        COVER_FIELDS.map((k) => [k, draft[k] || (k === "tone" ? "slate" : "")]),
      );
      const { data } = await api.put(
        `/admin/cover-pages/${activeMode}`,
        payload,
      );
      toast.success(`${MODE_META[activeMode].label} cover saved`);
      setModes((prev) =>
        prev.map((m) => (m.mode === activeMode ? data : m)),
      );
      setDrafts((d) => ({ ...d, [activeMode]: { ...data } }));
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Failed to save cover.",
      );
    } finally {
      setSavingMode(null);
    }
  };

  const onReset = async () => {
    // eslint-disable-next-line no-alert
    if (
      !window.confirm(
        `Reset the ${MODE_META[activeMode].label} cover to defaults? This clears any saved content for this mode.`,
      )
    ) {
      return;
    }
    setResettingMode(activeMode);
    try {
      const { data } = await api.delete(`/admin/cover-pages/${activeMode}`);
      toast.success("Reset to default");
      setModes((prev) =>
        prev.map((m) => (m.mode === activeMode ? data : m)),
      );
      setDrafts((d) => ({ ...d, [activeMode]: { ...data } }));
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Failed to reset cover.",
      );
    } finally {
      setResettingMode(null);
    }
  };

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(
        "/admin/cover-pages/upload-image",
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setField("image_url", data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Failed to upload image.",
      );
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const isCoverOn = siteMode !== "live";

  const switchSiteMode = async (target, successMsg) => {
    setTogglingSite(true);
    const previous = siteMode;
    setSiteMode(target);
    try {
      await api.put("/admin/integrations/SITE_MODE", { value: target });
      toast.success(successMsg);
    } catch (err) {
      setSiteMode(previous);
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Failed to update site mode.",
      );
    } finally {
      setTogglingSite(false);
    }
  };

  const toggleCover = (next) =>
    switchSiteMode(
      next ? activeMode : "live",
      next
        ? `Cover is ON — visitors will see "${MODE_META[activeMode].label}"`
        : "Cover is OFF — site is live",
    );

  const activateActiveMode = () => {
    if (!isCoverOn || siteMode === activeMode) return;
    switchSiteMode(
      activeMode,
      `Now showing "${MODE_META[activeMode].label}" to visitors`,
    );
  };

  return (
    <AdminLayout
      title="Cover pages"
      subtitle="Customise the splash screen visitors see for Coming soon, Maintenance and Custom modes. Switch between modes from the Integrations panel."
    >
      {loading ? (
        <div
          className="flex items-center gap-2 text-slate-500 text-sm"
          data-testid="admin-cover-loading"
        >
          <Loader2 className="h-4 w-4 animate-spin" /> Loading cover pages…
        </div>
      ) : (
        <div className="space-y-6" data-testid="admin-cover-content">
          <CoverToggleCard
            isCoverOn={isCoverOn}
            siteMode={siteMode}
            togglingSite={togglingSite}
            onToggle={toggleCover}
          />

          <CoverModeTabs
            activeMode={activeMode}
            onSelect={setActiveMode}
            isCoverOn={isCoverOn}
            siteMode={siteMode}
            onActivate={activateActiveMode}
            togglingSite={togglingSite}
          />

          <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
            <CoverEditor
              draft={draft}
              saved={saved}
              setField={setField}
              onSave={onSave}
              onReset={onReset}
              onUpload={onUpload}
              uploading={uploading}
              fileInputRef={fileInputRef}
              validTones={validTones}
              savingActive={savingMode === activeMode}
              resettingActive={resettingMode === activeMode}
              isDirty={isDirty}
            />

            <Card
              className="xl:col-span-2 overflow-hidden"
              testid="admin-cover-preview-card"
            >
              <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between bg-slate-50/60">
                <span className="text-xs font-medium uppercase tracking-[0.15em] text-slate-500">
                  Live preview
                </span>
                <span className="text-[11px] text-slate-400">
                  Visitor-facing splash
                </span>
              </div>
              <CoverPreview draft={draft} />
            </Card>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
